import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, Sparkles, FileText, CalendarDays, ArrowLeft, AlertTriangle } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import SectionCTA from "@/components/SectionCTA";
import { SERVICES } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-detail-${service.slug}`}>
      <Seo title={`${service.title} in Stockport & North West`} description={`${service.title} by Rossi Ceramics — ${service.short}`} />
      <PageHero eyebrow={service.tagline} title={service.title} subtitle={service.short} image={service.hero}>
        <Link to="/quote" className="btn-gold"><FileText size={16} /> Get a Quote</Link>
        <Link to="/booking" className="btn-ghost-gold"><CalendarDays size={16} /> Book a Consultation</Link>
        {service.aiCta && <Link to="/studio" className="btn-ghost-gold"><Sparkles size={16} /> Design in The Studio</Link>}
      </PageHero>

      <section className="container-rossi py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          <Reveal>
            <Link to="/services" className="inline-flex items-center gap-1 text-sm text-rossi-stone hover:text-rossi-gold transition-colors"><ArrowLeft size={15} /> All services</Link>
            <h2 className="font-serif text-3xl text-rossi-cream mt-4">What's included</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-3">
            {service.features.map((f) => (
              <div key={f} className="flex items-center gap-3 card-lux p-4">
                <span className="h-7 w-7 shrink-0 rounded-full bg-rossi-gold/15 text-rossi-gold flex items-center justify-center"><Check size={15} /></span>
                <span className="text-sm text-rossi-cream/85">{f}</span>
              </div>
            ))}
          </div>

          {service.note && (
            <div className="flex gap-3 rounded-xl border border-rossi-gold/25 bg-rossi-gold/5 p-5 text-sm text-rossi-cream/80">
              <AlertTriangle size={18} className="text-rossi-gold shrink-0 mt-0.5" />
              <p>{service.note}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4">
            {service.images.map((img, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <img src={img} alt={`${service.title} example ${i + 1} by Rossi Ceramics`} className="rounded-2xl w-full h-56 object-cover border border-rossi-gold/15" />
              </Reveal>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="card-lux p-7 sticky top-24">
            <h3 className="font-serif text-2xl text-rossi-cream">Start your {service.title.toLowerCase()}</h3>
            <p className="mt-2 text-sm text-rossi-cream/70">Free, no-obligation quotes across Stockport and the North West.</p>
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/quote" className="btn-gold w-full"><FileText size={16} /> Get a Free Quote</Link>
              <Link to="/booking" className="btn-ghost-gold w-full"><CalendarDays size={16} /> Book a Consultation</Link>
              {service.aiCta && <Link to="/studio" className="btn-ghost-gold w-full"><Sparkles size={16} /> Design in The Studio</Link>}
            </div>
          </div>
        </aside>
      </section>

      <SectionCTA />
    </div>
  );
}
